import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  Output,
  SimpleChanges,
  EventEmitter,
  inject,
  ViewChild,
} from '@angular/core';
import { SubsectionService } from '../../../shared/services/subsection.service';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MTMFileDownload } from '../../../shared/services/signed-url/mtm-file-download';
import { AuthService } from '../../../shared/services/auth.service';
import { FileTransferService } from '../../../shared/services/signed-url';
import { TopMediaInfoService } from '../../../shared/components/top-media-info/top-media-info.service';
import { ActiveSubsectionState, SimpleFilePage, SimpleFilePages } from '../../../shared/interfaces';

@Component({
  selector: 'mtm-pdf-slider-component',
  templateUrl: './mtm-pdf-slider.component.html',
  styleUrl: './mtm-pdf-slider.component.scss',
  standalone: false,
})
export class MtmPdfSliderComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pagesViewport', { static: false }) pagesViewport: CdkVirtualScrollViewport;

  private readonly subsectionService = inject(SubsectionService);
  private readonly authService = inject(AuthService);
  private readonly transferService = inject(FileTransferService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly ngUnsubscribe = new Subject<void>();
  private retryRecord: Record<string, number> = {};
  commentCount: Record<string, number> = {};

  @Input() filePages: SimpleFilePages | null = null;
  @Input() activePage: SimpleFilePage | null = null;
  @Input() isWidgetMode: boolean = false;
  @Output() pageClick = new EventEmitter<Partial<ActiveSubsectionState>>();

  @Input() collapsed: boolean = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  pages: any;
  pageNumberClicked: number = 0;
  loadingElement: boolean = false;
  isShareLinkPreview: boolean = false;
  isReleasePreview: boolean = false;

  get isFullscreen() {
    return this.topMediaInfoService.isFullscreen;
  }

  ngOnInit(): void {
    this.subsectionService.commentCount$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (commentCount) => {
        this.commentCount = commentCount;
      },
    });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.isShareLinkPreview === curr?.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state?.isShareLinkPreview || false;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.isReleasePreview === curr?.isReleasePreview)
      )
      .subscribe({
        next: (state) => {
          this.isReleasePreview = state?.isReleasePreview || false;
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.filePages?.currentValue || changes.activePage?.currentValue) &&
      this.activePage?.rank != this.pageNumberClicked
    ) {
      setTimeout(() => {
        this.scrollToCurrentPage();
      }, 300);
    }
  }

  scrollToCurrentPage() {
    if (!this.activePage || !this.pagesViewport) {
      return;
    }

    this.pagesViewport.scrollToIndex(this.activePage.rank - 1, 'smooth');
    this.pageNumberClicked = this.activePage.rank;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  toggleCollapsed(): void {
    this.collapsedChange.emit(!this.collapsed);
  }

  pageSelected(page) {
    this.pageNumberClicked = page.rank;

    if (this.isWidgetMode) {
      this.pageClick.emit({ activePage: page });
    } else {
      this.subsectionService.updateSubsectionState({ activePage: page });
    }
  }

  retryLoad(event: any) {
    const url = event.target.src;
    const retryCount = this.retryRecord[url] || 0;
    if (retryCount > 3) {
      return;
    }

    setTimeout(() => {
      const date = Date.now();
      event.target.src = url + '&retry=' + date;
      this.retryRecord[url] = retryCount + 1;
    }, 3000);
  }

  trackByPageId(index: number, page: SimpleFilePage) {
    return page.id;
  }

  hasCommentCount(key: string): boolean {
    return key in this.commentCount && this.commentCount[key] > 0;
  }

  downloadPage(page: SimpleFilePage) {
    const downloadItem = new MTMFileDownload();
    downloadItem.name = page.name;
    downloadItem.url = page.signedURL;
    downloadItem.contentType = 'image/png';
    downloadItem.displayName = page.name;
    this.transferService.downloadFile(downloadItem);
  }
}
